import type { PostHog } from 'posthog-js'

interface PostHogUser {
  customerId?: string
  email?: string
  name?: string
  planType?: string
  accountType?: string
  subscriptionStatus?: string
}

interface PostHogProperties {
  [key: string]: any
}

export const usePostHog = () => {
  const { $posthog } = useNuxtApp() as { $posthog: PostHog }

  // Identify user with authentication data
  const identifyUser = (user: PostHogUser) => {
    if (!$posthog || !user.customerId) return

    $posthog.identify(user.customerId, {
      email: user.email,
      name: user.name,
      plan_type: user.planType,
      account_type: user.accountType,
      subscription_status: user.subscriptionStatus,
      $set: {
        first_seen: new Date().toISOString(),
      },
      $set_once: {
        signup_date: new Date().toISOString(),
      }
    })

    console.log('[PostHog] User identified:', user.customerId)
  }

  // Track custom events
  const track = (event: string, properties?: PostHogProperties) => {
    if (!$posthog) return

    $posthog.capture(event, {
      ...properties,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      screen_resolution: `${screen.width}x${screen.height}`,
    })

    console.log('[PostHog] Event tracked:', event, properties)
  }

  // Track page views manually
  const trackPageView = (path?: string) => {
    if (!$posthog) return

    // Use passed path parameter to avoid useRoute in middleware context
    const currentPath = path || (typeof window !== 'undefined' ? window.location.pathname : '')

    $posthog.capture('$pageview', {
      $current_url: currentPath,
      title: typeof document !== 'undefined' ? document.title : '',
      referrer: typeof document !== 'undefined' ? document.referrer : '',
    })
  }

  // Track ecommerce events
  const trackPurchase = (amount: number, currency: string = 'AUD', planId?: string, planName?: string) => {
    track('purchase_completed', {
      revenue: amount,
      currency,
      plan_id: planId,
      plan_name: planName,
    })
  }

  const trackAddToCart = (planId: string, planName: string, price: number) => {
    track('add_to_cart', {
      plan_id: planId,
      plan_name: planName,
      price,
    })
  }

  // Track authentication events
  const trackLogin = (method: string = 'email') => {
    track('user_logged_in', {
      login_method: method,
    })
  }

  const trackLogout = () => {
    track('user_logged_out')
    
    // Reset PostHog user
    if ($posthog) {
      $posthog.reset()
    }
  }

  // Track form interactions
  const trackFormSubmission = (formName: string, success: boolean, errorMessage?: string) => {
    track('form_submitted', {
      form_name: formName,
      success,
      error_message: errorMessage,
    })
  }

  // Track API errors for debugging
  const trackApiError = (endpoint: string, statusCode: number, errorMessage: string) => {
    track('api_error', {
      endpoint,
      status_code: statusCode,
      error_message: errorMessage,
      user_id: $posthog?.get_distinct_id(),
    })
  }

  // Feature flag helpers
  const isFeatureEnabled = (flagKey: string): boolean => {
    if (!$posthog) return false
    return $posthog.isFeatureEnabled(flagKey) || false
  }

  const getFeatureFlag = (flagKey: string) => {
    if (!$posthog) return undefined
    return $posthog.getFeatureFlag(flagKey)
  }

  // A/B testing helper
  const getVariant = (experimentKey: string): string | undefined => {
    if (!$posthog) return undefined
    return $posthog.getFeatureFlag(experimentKey) as string
  }

  // Track user properties update
  const updateUserProperties = (properties: PostHogProperties) => {
    if (!$posthog) return

    $posthog.people.set(properties)
  }

  // Track user journey/funnel events
  const trackFunnelStep = (funnelName: string, step: string, stepNumber: number) => {
    track('funnel_step', {
      funnel_name: funnelName,
      step_name: step,
      step_number: stepNumber,
    })
  }

  return {
    // Core functions
    identifyUser,
    track,
    trackPageView,
    
    // Ecommerce tracking
    trackPurchase,
    trackAddToCart,
    
    // Authentication tracking
    trackLogin,
    trackLogout,
    
    // Form tracking
    trackFormSubmission,
    
    // Error tracking
    trackApiError,
    
    // Feature flags
    isFeatureEnabled,
    getFeatureFlag,
    getVariant,
    
    // User properties
    updateUserProperties,
    
    // Funnel tracking
    trackFunnelStep,
    
    // Direct PostHog access
    posthog: $posthog,
  }
}
